import React from 'react';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm } from 'react-hook-form';
import { DocumentNode, InternalRefetchQueryDescriptor, useMutation } from '@apollo/client';

import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import EntityTerminationActionItemEditForm from './EntityTerminationActionItemEditForm';
import { IEntityTerminationActionItem } from '../types';

interface IEntityTerminationActionItemEditPanelProps {
  closePanel: () => void;
  isNewActionItem?: boolean;
  selectedActionItem?: IEntityTerminationActionItem;
  companyCode: string;
  refetchQueries: InternalRefetchQueryDescriptor[];
  updateMutation: DocumentNode;
  /** Provide when the stage supports creating new action items (e.g. Legal In Process). */
  createMutation?: DocumentNode;
  /** GraphQL variable name for the action item input DTO. */
  dtoVariableName: string;
  /** When true, the description is template-based: shown read-only and omitted from the payload. */
  isDescriptionReadOnly?: boolean;
}

const EntityTerminationActionItemEditPanel = (
  props: IEntityTerminationActionItemEditPanelProps,
): JSX.Element => {
  const {
    closePanel,
    isNewActionItem = false,
    selectedActionItem,
    companyCode,
    refetchQueries,
    updateMutation,
    createMutation,
    dtoVariableName,
    isDescriptionReadOnly = false,
  } = props;
  const form = useForm();
  const { handleSubmit } = form;

  const title = isNewActionItem ? 'Create New ' : 'Edit ';

  const [createActionItem] = useMutation(createMutation ?? updateMutation, {
    refetchQueries,
    onCompleted: () => {
      closePanel();
    },
  });

  const [updateActionItem] = useMutation(updateMutation, {
    refetchQueries,
    onCompleted: () => {
      closePanel();
    },
  });

  const handleSaveClick = (formData: IEntityTerminationActionItem): void => {
    const ownerInput = formData.owner
      ? {
          id: formData.owner.id,
          graphId: formData.owner.graphId,
          name: formData.owner.name,
          firstName: formData.owner.firstName,
          lastName: formData.owner.lastName,
          email: formData.owner.email,
          alias: formData.owner.alias,
        }
      : null;

    const actionItemVariables = {
      [dtoVariableName]: {
        id: selectedActionItem?.id ?? null,
        companyCode,
        owner: ownerInput,
        dateStarted: formData.dateStarted,
        status: formData.status,
        dateCompleted: formData.dateCompleted || null,
        ...(isDescriptionReadOnly ? {} : { description: formData.description }),
      },
      companyCode,
    };

    if (isNewActionItem) {
      createActionItem({ variables: { ...actionItemVariables } });
    } else {
      updateActionItem({ variables: { ...actionItemVariables } });
    }
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText={`${title} Action Item`}
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          saveLabel="Save Action Item"
          saveTitle="Save Action Item"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
        />
      )}
    >
      <form>
        <EntityTerminationActionItemEditForm
          form={form}
          selectedActionItem={selectedActionItem}
          isDescriptionReadOnly={isDescriptionReadOnly}
        />
      </form>
    </CoherencePanel>
  );
};

export default EntityTerminationActionItemEditPanel;
